import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { sortBy } from 'lodash'
import { Text, Tab, Separator, Card, CardColors, Loader } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { fetchUserTransactions } from '@/features/transactions/transactions.slice'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import {
  fetchProfitsBreakdown,
  fetchExpensesBreakdown,
  fetchUncategorizedBreakdown,
  Expense,
  fetchOtherBreakdown,
} from '@/actions/reportActions'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { selectActiveReportByDate } from '@/selectors/reportSelectors'
import { DateTime } from 'luxon'

import { useReselector } from '@/utils/sharedHooks'
import { selectBookkeepingReportList } from '@/selectors/bookkeepingReportsSelectors'
import { fetchBookkeepingReports } from '@/actions/bookkeepingReportActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'
import { PageHeader } from '@/components/PageHeader'
import { BOOKKEEPING_REPORT_STATUSES } from '@/constants/businessConstants'
import { selectTransactionsNeedReviewForMonth } from '@/features/transactions/transactions.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { FreeTrialUnreviewedAlert } from '@/components/FreeTrialUnreviewedAlert'
import { LineItem, TotalLineItem } from './LineItem'
import { ReportInProgress } from './ReportInProgress'
import { EoyPromptCard } from './EoyPromptCard'
import { NoReportAlert } from './NoReportAlert'
import { NeedsReviewAlert } from './NeedsReviewAlert'
import { CashflowBarGraph } from '../CashflowBarGraph'
import { BookkeepingReportTopExpensesCard } from '../BookkeepingReportTopExpensesCard'
import { MonthPicker } from '@/features/financial-insights/reports/MonthPicker'
import { ReportsTransactionTable } from '@/features/financial-insights/reports/ReportsTransactionTable'

import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import {
  UserActionItemActionItemIdentifiers,
  fetchUserActionItemsIfNeeded,
} from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { eoyReviewReviewPeriodNotActive } from '@/features/user/dashboard/user-action-items/lib/userActionItems.selectors'
import { MissingStatements } from '@/features/user/your-practice/documents/shared/MissingStatements'

// TODO: Migrate to V2
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'

const TAB_INDEXES: { [key: number]: string } = {
  0: 'overview',
  1: 'profit-loss',
}

////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////
// We expect startDate and endDate as: YYYY-MM-DD format
///////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////

export const BookkeepingReportPanel = () => {
  const dispatch = useAppDispatch()
  const { reportName, bookkeepingReportMonth, bookkeepingReportYear } =
    useParams()
  const navigate = useNavigate()
  const defaultActiveIndex = reportName === 'profit-loss' ? 1 : 0

  const pageView = useAnalyticsView()

  useEffect(() => {
    if (reportName === 'overview') pageView('monthly bookkeeping report')
  }, [pageView, reportName])

  const bookkeepingReports = useReselector(selectBookkeepingReportList)
  const activeReport = useReselector(
    selectActiveReportByDate,
    `${bookkeepingReportYear}-${bookkeepingReportMonth}`
  )

  const year = useReselector(selectCurrentAnnualTaxYear)
  const eoyPeriodInactive = useReselector(eoyReviewReviewPeriodNotActive, year)
  const startDate = activeReport
    ? DateTime.fromISO(activeReport.startDate, { zone: 'utc' })
    : null
  const endDate = activeReport
    ? DateTime.fromISO(activeReport.endDate, { zone: 'utc' })
    : null
  const currentStatus = activeReport?.statuses[0]
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  /**
   * Report Status: Only used for Paying users
   * For 14 day Trial Users: Always show report as there is no BK support
   */
  const reportReconciled = isInUnservicedFreeTrial
    ? true
    : currentStatus?.status &&
      [
        BOOKKEEPING_REPORT_STATUSES.Reconciled.name,
        BOOKKEEPING_REPORT_STATUSES.Done.name,
      ].includes(currentStatus?.status)

  const reportDone = isInUnservicedFreeTrial
    ? true
    : currentStatus?.status === BOOKKEEPING_REPORT_STATUSES.Done.name
  const track = useAnalyticsTrack()
  const needsReviewTransactions = useReselector(
    selectTransactionsNeedReviewForMonth,
    activeReport?.date
  )

  const [loading, setLoading] = useState(true)
  const [expensesObj, setExpensesObj] = useState<{
    expenses: Expense[]
    sum: number
  }>({
    expenses: [],
    sum: 0,
  })

  const [profitsObj, setProfitsObj] = useState<{
    profits: Expense[]
    sum: number
  }>({
    profits: [],
    sum: 0,
  })

  const [otherObj, setOtherObj] = useState<{
    otherExpenses: Expense[]
    sum: number | string
  }>({
    otherExpenses: [],
    sum: 0,
  })

  const [uncatObj, setUncatObj] = useState<{
    sum: number
    transactionCategoryId: number | null
  } | null>(null)

  useEffect(() => {
    const fetch = async () => {
      await Promise.all([
        dispatch(fetchBookkeepingReports()),
        dispatch(fetchUserTransactions()),
        dispatch(fetchTransactionCategoriesIfNeeded()),
        dispatch(fetchUserDocuments()),
        dispatch(fetchUserActionItemsIfNeeded()),
      ])

      setLoading(false)
    }

    fetch()
  }, [dispatch])

  const markActionItemComplete = useCallback(async () => {
    const dateParts = activeReport?.date.split('-')
    if (dateParts) {
      // Make remove leading 0 if exists
      const month =
        dateParts[1]?.[0] === '0' ? dateParts[1].substring(1) : dateParts[1]

      await markUserActionItemCompleteIfExists(
        UserActionItemActionItemIdentifiers.reviewBooks({
          month,
          year: dateParts[0],
        }),
        (event, properties) => track(event, properties)
      )
    }
  }, [activeReport?.date, track])

  useEffect(() => {
    const fetch = async () => {
      if (!activeReport) {
        return
      }

      setLoading(true)
      const params = {
        startDate: DateTime.fromISO(activeReport.startDate, { zone: 'utc' }),
        endDate: DateTime.fromISO(activeReport.endDate, { zone: 'utc' }),
      }
      const profits = await fetchProfitsBreakdown(params)()
      const expenses = await fetchExpensesBreakdown(params)(dispatch)
      const other = await fetchOtherBreakdown(params)(dispatch)
      const unCat = await fetchUncategorizedBreakdown(params)()

      setProfitsObj(profits)
      setExpensesObj(expenses)
      setOtherObj(other)
      setUncatObj(unCat)

      await markActionItemComplete()
      setLoading(false)
    }

    fetch()
  }, [activeReport, dispatch, markActionItemComplete])

  const updateTab = (newIndex: number) => {
    const newTab = TAB_INDEXES[newIndex]
    navigate(
      `/reports/bookkeeping/${bookkeepingReportMonth}/${bookkeepingReportYear}/${newTab}`,
      {
        replace: true,
      }
    )
  }

  const profitsAndLoss = useMemo(() => {
    const netIncome = Number(profitsObj?.sum) + Number(expensesObj?.sum) || 0

    return (
      <Card color={CardColors.EMPHASIS} className="space-y-6">
        <Text variant={TypographyStyles.DISPLAY_XS}>
          Profit and Loss Statement
        </Text>

        <Text variant={TypographyStyles.DISPLAY_XXS}>Income</Text>
        {profitsObj.profits.map((profit) => (
          <LineItem item={profit} key={profit.transactionCategoryId} />
        ))}
        <TotalLineItem label="Total Income" sum={profitsObj?.sum} />

        <Text variant={TypographyStyles.DISPLAY_XXS}>Expenses</Text>
        {expensesObj.expenses.map((expense) => (
          <LineItem item={expense} key={expense.transactionCategoryId} />
        ))}
        <TotalLineItem label="Total Expenses" sum={expensesObj?.sum} />

        <TotalLineItem label="Net Profit" sum={netIncome} />

        <Text variant={TypographyStyles.DISPLAY_XXS}>Other</Text>
        {otherObj.otherExpenses.map((expense) => (
          <LineItem item={expense} key={expense.transactionCategoryId} />
        ))}
        <TotalLineItem label="Total Other" sum={otherObj?.sum} />

        <Separator className="my-6" />

        <Text variant={TypographyStyles.DISPLAY_XXS}>To Be Categorized</Text>
        <Text variant={TypographyStyles.BODY_SM}>
          Don&apos;t worry about these— your bookkeeper will categorize these
          transactions each month.
        </Text>
        {uncatObj && <LineItem item={uncatObj} />}
      </Card>
    )
  }, [
    expensesObj.expenses,
    expensesObj?.sum,
    otherObj.otherExpenses,
    otherObj?.sum,
    profitsObj.profits,
    profitsObj?.sum,
    uncatObj,
  ])

  const reports = useMemo(() => {
    if (!startDate) {
      return null
    }

    const formattedMonth = startDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="w-full">
            <CashflowBarGraph
              monthView
              currentMonth={formattedMonth}
              dropdownDisabled
            />
          </div>
          <div className="w-full">
            <BookkeepingReportTopExpensesCard
              loading={loading}
              expensesObject={expensesObj}
            />
          </div>
        </div>

        {startDate && endDate && (
          <ReportsTransactionTable
            formattedMonth={formattedMonth}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </div>
    )
  }, [endDate, expensesObj, loading, startDate])

  const bookkeepingIncompleteStatusText = useMemo(() => {
    if (!startDate || !activeReport) {
      return null
    }
    return (
      <MissingStatements includeUploadButton whenNone={<ReportInProgress />} />
    )
  }, [startDate, activeReport])

  const reconciledReportAlert = useMemo(() => {
    if (!activeReport || reportDone) {
      return null
    }

    if (needsReviewTransactions?.count && needsReviewTransactions.count > 0) {
      return (
        <NeedsReviewAlert
          needsReviewTransactionsCount={needsReviewTransactions.count}
          startDate={startDate}
          endDate={endDate}
        />
      )
    }
    return <ReportInProgress />
  }, [
    activeReport,
    needsReviewTransactions?.count,
    reportDone,
    startDate,
    endDate,
  ])

  // Show loader if we have no data, or if reports are loaded but activeReport hasn't resolved yet
  if (
    loading ||
    (bookkeepingReportMonth && bookkeepingReportYear && !activeReport)
  ) {
    return (
      <div id="bookkeepingReportPanel">
        <Loader active inline />
      </div>
    )
  }
  // If user has no bookkeeping reports (new user), show a not yet screen
  else if (bookkeepingReports?.length === 0) {
    return <NoReportAlert />
  } else if (!bookkeepingReportMonth || !bookkeepingReportYear) {
    // If there is no explicit start and end date, let's pull the most recent report and redirect
    const sortedReports = sortBy(bookkeepingReports, 'date')
    const lastReport = sortedReports[sortedReports.length - 1]
    const lastReportDate = DateTime.fromFormat(
      lastReport.date,
      DATE_FORMATS_LUXON.YEAR_MONTH
    )
    return (
      <Navigate
        to={`/reports/bookkeeping/${lastReportDate.toFormat(DATE_FORMATS_LUXON.MONTH_ZERO_PADDED)}/${lastReportDate.toFormat(DATE_FORMATS_LUXON.YEAR_FOUR_DIGIT)}/overview`}
        replace
      />
    )
  }

  // Otherwise, we are at a stage where we can display the report
  // We want to render report only if the report is ready
  return (
    <>
      {!eoyPeriodInactive && <EoyPromptCard />}
      <div id="bookkeepingReportPanel">
        {startDate && (
          <PageHeader
            header={startDate.toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)}
            extraContent={
              activeReport &&
              startDate && <MonthPicker activeMonth={startDate} />
            }
          />
        )}
        <div className="p-6 pt-0 md:p-0">
          {reportReconciled && currentStatus && (
            <>
              <Text color={TextColors.MOSS} className="mb-4">
                This report was compiled on{' '}
                {DateTime.fromISO(currentStatus.createdAt, {
                  zone: 'utc',
                }).toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}
              </Text>
              {reconciledReportAlert}
            </>
          )}
        </div>
        <FreeTrialUnreviewedAlert />
        {reportReconciled && (
          <Tab
            defaultActiveIndex={defaultActiveIndex}
            onTabClick={updateTab}
            variant="outline"
            panes={[
              {
                menuItem: 'Overview',
                render: () => reports,
              },
              {
                menuItem: 'Profit and Loss',
                render: () => profitsAndLoss,
              },
            ]}
          />
        )}
        {!reportReconciled && currentStatus && (
          <div className="mt-6">{bookkeepingIncompleteStatusText}</div>
        )}
      </div>
    </>
  )
}
